import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import DiscoverAlbumFolderSummary from '../../components/discover/DiscoverAlbumFolderSummary';
import {
  MeForPrintCheckout,
  MeWithIdentities,
  UserAnalyticsFunnelData,
} from '../../graphql/queries/account';
import { albumsJoinByCode } from '../../graphql/mutations/albums';
import {
  onDeselectAlbum,
  checkoutAlbums,
  setJoinTypeSelectionFlag,
  onSelectIdentity,
  onDeselectAlbumCustomPrint,
  onSelectAlbumCustomPrint,
  onDeselectIdentity,
} from '../../actions/discover-album-folder';
import { AlbumFolderWithChildrenAndSubscription } from '../../graphql/queries/album-folder';
import { checkoutPrice } from '../../graphql/queries/checkout';
import { getAlbumFolderDiscoveryOnboardingItems } from '../../util/discover-album-folder';
import { checkout } from '../../graphql/mutations/checkout';
import { openModal } from '../../actions/modal-actions';
import {
  checkoutCartRequiresAddress,
  getCheckoutCartCheckoutItems,
} from '../../services/checkout/redux/checkoutSelectors';
import { isPrepaidOnlyAlbumFolder } from '../../util/album-folder';
import {
  clearCart,
  getIdentityCartItems,
  onIdentityCartItemsRemove,
  setActiveIdentity,
} from '../../services/checkout/redux/checkoutActions';

const withUserIdentities = graphql(MeWithIdentities, {
  props: ({ data: { me } }) => ({
    user: me,
    identities: me?.linked_identities?.records,
  }),
});

const withAlbumsJoinByCode = graphql(albumsJoinByCode, {
  props: ({ mutate, ownProps }) => ({
    albumsJoinByCode: (accountId, joinCodes, identities) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            join_codes: joinCodes,
            identities,
          },
        },
        refetchQueries: [
          {
            query: UserAnalyticsFunnelData,
            variables: {
              albumFolderId: ownProps.match.params.albumFolderId,
            },
          },
        ],
      }),
  }),
});

const withCheckout = graphql(checkout, {
  props: ({ mutate, ownProps }) => ({
    checkout: ({
      accountId,
      accountAddressId,
      email,
      stripeToken,
      ppaItems,
      photoItems,
      customPrintItems,
      photoDownloadItems,
      digitalBundleItems,
      searchSubscriptionItems,
      stripePaymentMethodId,
      stripeConnectAccountId,
      promoCode = '',
    }) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            account_address_id: accountAddressId,
            email_address: email,
            stripe_token: stripeToken,
            photo_items: photoItems,
            subscription_items: ppaItems,
            custom_print_items: customPrintItems,
            photo_download_items: photoDownloadItems,
            search_subscription_items: searchSubscriptionItems,
            stripe_payment_method_id: stripePaymentMethodId,
            stripe_connect_account_id: stripeConnectAccountId,
            digital_bundle_items: digitalBundleItems,
            promo_code: promoCode,
            platform: 'WEB',
          },
        },
        refetchQueries: [
          {
            query: UserAnalyticsFunnelData,
            variables: {
              albumFolderId: ownProps.match.params.albumFolderId,
            },
          },
        ],
      }),
  }),
});

const withAlbumFolder = graphql(AlbumFolderWithChildrenAndSubscription, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder, loading } = {} }) => ({
    albumFolder,
    loading,
  }),
});

const mapStateToProps = state => {
  const { discoverAlbumFolder, photoUploads } = state;

  return {
    selectedIdentities: discoverAlbumFolder.identities,
    storePhotos: photoUploads.photos,
    loading: discoverAlbumFolder.loading,
    error: discoverAlbumFolder.error,
    success: discoverAlbumFolder.success,
    prepaidCheckoutItems: getCheckoutCartCheckoutItems(state),
    prepaidRequiresAddress: checkoutCartRequiresAddress(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onDeselectAlbum: (album, identity) => {
    if (
      identity.selectedAlbums.length === 1 &&
      identity.selectedAlbums?.[0]?.id === album.id
    ) {
      dispatch(onDeselectIdentity(identity));
      dispatch(onIdentityCartItemsRemove({ identityId: identity.id }));
    } else {
      dispatch(onDeselectAlbum(album, identity.id));
    }
  },
  onDeselectAlbumCustomPrint: (albumCustomPrint, identityId) =>
    dispatch(onDeselectAlbumCustomPrint(albumCustomPrint, identityId)),
  onSelectAlbumCustomPrint: (albumCustomPrint, identityId) =>
    dispatch(onSelectAlbumCustomPrint(albumCustomPrint, identityId)),
  checkoutAlbums: ({
    accountId,
    accountAddressId,
    email,
    stripeToken,
    purchaseItems,
    joinItems,
    stripePaymentMethodId,
    stripeConnectAccountId,
    promoCode,
  }) =>
    dispatch(
      checkoutAlbums({
        accountId,
        accountAddressId,
        email,
        stripeToken,
        purchaseItems,
        joinItems,
        checkoutMutation: ownProps.checkout,
        albumsJoinByCodeMutation: ownProps.albumsJoinByCode,
        stripePaymentMethodId,
        stripeConnectAccountId,
        promoCode,
      }),
    ),
  setJoinTypeSelectionFlag: flag => dispatch(setJoinTypeSelectionFlag(flag)),
  onSelectIdentity: (identity, joinType) =>
    dispatch(onSelectIdentity(identity, joinType)),
  onDeselectIdentity: identity => {
    dispatch(onDeselectIdentity(identity));
    dispatch(onIdentityCartItemsRemove({ identityId: identity.id }));
  },
  getIdentityCartItems: ({ identityId }) =>
    dispatch(getIdentityCartItems({ identityId })),
  setActiveIdentity: identity => dispatch(setActiveIdentity(identity)),
  openModal: (...args) => dispatch(openModal(...args)),
  clearCart: (...args) => dispatch(clearCart(...args)),
});

const withCheckoutPrice = graphql(checkoutPrice, {
  options: ({
    albumFolder,
    prepaidCheckoutItems,
    selectedIdentities,
    user,
    userAddress,
  }) => {
    const {
      checkoutPpaItems,
      customPrintItems,
    } = getAlbumFolderDiscoveryOnboardingItems(selectedIdentities);

    const checkoutPriceVariables = isPrepaidOnlyAlbumFolder(albumFolder)
      ? { ...prepaidCheckoutItems, subscription_items: checkoutPpaItems }
      : {
          subscription_items: checkoutPpaItems,
          custom_print_items: customPrintItems,
        };

    return {
      variables: {
        account_id: user?.id,
        address_id: userAddress?.id,
        ...checkoutPriceVariables,
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    };
  },
  props: ({
    data: { checkoutPrice: price, loading, error, refetch } = {},
  }) => ({
    loadingCheckoutPrice: loading,
    checkoutPrice: price,
    errorLoadingPrice: error,
    refetchCheckoutPrice: refetch,
  }),
  skip: ({ user, selectedIdentities, loadingUserAddress }) =>
    loadingUserAddress ||
    !user ||
    !selectedIdentities ||
    !selectedIdentities.length,
});

const withUserAddress = graphql(MeForPrintCheckout, {
  props: ({ data: { me, loading } }) => {
    const [userAddress] = me?.addresses ?? [];

    return {
      userAddress,
      loadingUserAddress: loading,
    };
  },
});

export default compose(
  withRouter,
  withUserIdentities,
  withUserAddress,
  withAlbumFolder,
  withAlbumsJoinByCode,
  withCheckout,
  connect(mapStateToProps, mapDispatchToProps),
  withCheckoutPrice,
)(DiscoverAlbumFolderSummary);
